import { Search, X } from 'lucide-react';
import * as React from 'react';

import { cn } from '../cn';

export interface SearchInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Called with the empty string when the clear (×) button is pressed. */
  onClear?: () => void;
  clearLabel?: string;
}

/**
 * A text input with a leading search glyph and, when non-empty, a trailing
 * clear button. Wraps the same bordered/gold-focus field styling as `Input`.
 */
export const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  ({ className, value, onClear, clearLabel, ...props }, ref) => {
    const hasValue = typeof value === 'string' && value.length > 0;
    return (
      <div className="relative">
        <Search
          className="pointer-events-none absolute inset-y-0 start-3 my-auto h-4 w-4 text-muted"
          aria-hidden
        />
        <input
          ref={ref}
          type="search"
          value={value}
          className={cn(
            'h-9 w-full rounded-sm border border-border bg-bg ps-9 pe-9 text-body text-ink',
            'placeholder:text-muted',
            'transition-colors duration-150 motion-reduce:transition-none',
            'focus-visible:outline-none focus-visible:border-gold focus-visible:ring-2 focus-visible:ring-gold/20',
            // Hide the native search-clear affordance; we render our own.
            '[&::-webkit-search-cancel-button]:appearance-none',
            className,
          )}
          {...props}
        />
        {hasValue && onClear ? (
          <button
            type="button"
            onClick={onClear}
            aria-label={clearLabel ?? 'Clear'}
            className="absolute inset-y-0 end-2 my-auto flex h-6 w-6 items-center justify-center rounded-sm text-muted hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/20"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        ) : null}
      </div>
    );
  },
);
SearchInput.displayName = 'SearchInput';
